// MerchantSwitcher — topbar dropdown that sets the active merchant.
//
// Every merchant-scoped screen (Bank rails, API keys, Team, Payouts) keys off
// useUI().currentMerchantId. Without this control an admin has no merchant
// selected and those screens' primary actions are inert. Admins pick from the
// full merchant list; merchant users pick from their own memberships.
//
// AUDIT-PASS-6 M-B: commit to a single focus pattern — aria-activedescendant.
// The earlier implementation simultaneously ran *both* roving DOM focus and
// aria-activedescendant: every option button was physically `.focus()`'d on
// activeIdx change, AND the listbox declared `aria-activedescendant`. That
// hybrid has three concrete bugs:
//   1. `onMouseEnter={() => setActiveIdx(idx)}` on each option called into the
//      focus-following effect, so any mouse movement during keyboard nav
//      yanked DOM focus to whatever the cursor happened to be hovering.
//   2. Screen readers got conflicting cues — the focused button's accessible
//      name AND the aria-activedescendant target.
//   3. The new :focus-visible rings (audit-pass-6 UI-CRIT-1) would flash on
//      the option button as keyboard nav moved through it, which is wrong
//      under the activedescendant pattern (visual cue should come from the
//      `[aria-selected]` / focused-style on the descendant, not a focus ring
//      on a button that is supposed to be "logically focused but not DOM-
//      focused").
//
// Under aria-activedescendant: DOM focus stays on the listbox container for
// the entire open lifetime, and the "active" option is communicated to AT
// purely through the activedescendant pointer + visual styling. Option
// buttons are `tabIndex={-1}` and use `onMouseDown` preventDefault so a
// click commits the selection without first transferring focus to the
// button.
//
// Keyboard contract:
//   - Trigger:   Space/Enter or ArrowDown opens, focuses the listbox.
//   - Listbox:   ArrowUp/ArrowDown moves activeIdx; Home/End jump to
//                first/last; Enter/Space selects + closes; Escape closes
//                and returns focus to the trigger.
//   - Click-outside, click-option, Escape: all close and restore focus
//                to the trigger so the keyboard user is never stranded.
//   - Mouse hover: does NOT change activeIdx — the keyboard cursor and
//                the mouse cursor are independent.
import {
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
} from "react";
import { Link } from "react-router-dom";

import { I } from "@/components/Icons";
import { useUI } from "@/store/ui";
import { useAuth, isAdminRole } from "@/store/auth";
import { useMerchants } from "@/queries/useMerchants";

interface Option {
  id: number;
  slug: string | null;
  label: string;
}

export function MerchantSwitcher() {
  const user = useAuth((s) => s.user);
  const isAdmin = isAdminRole(user?.role);
  const currentMerchantId = useUI((s) => s.currentMerchantId);
  const currentMerchantSlug = useUI((s) => s.currentMerchantSlug);
  const setCurrentMerchant = useUI((s) => s.setCurrentMerchant);

  // useMerchants is admin-gated inside the hook — empty for merchant users.
  const { data: allMerchants = [] } = useMerchants();

  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const listboxRef = useRef<HTMLDivElement>(null);
  const listboxId = useId();

  // Click-outside close.
  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const options: Option[] = isAdmin
    ? allMerchants.map((m) => ({
        id: m.id,
        slug: m.slug ?? null,
        label: m.display_name,
      }))
    : (user?.memberships ?? []).map((m) => ({
        id: m.merchant_id,
        slug: m.slug ?? null,
        // Use display_name from /v1/me's joined-with-merchants response
        // when present (post-backend-fix). Fall back to bare
        // "Merchant #N · role" if an older backend version (or a
        // mid-rolling-deploy response) omits the field — never crash
        // because of a missing field.
        label: m.display_name
          ? `${m.display_name} · ${m.role}`
          : `Merchant #${m.merchant_id} · ${m.role}`,
      }));

  // Auto-select a default merchant when none is chosen yet — admins default
  // to the first merchant, merchant users to their first membership — so the
  // merchant-scoped pages (Bank rails, API keys, Team) are never a dead-end
  // on "No merchant selected".
  const firstOption = options[0];
  const firstOptionId = firstOption?.id;
  const firstOptionSlug = firstOption?.slug ?? null;
  useEffect(() => {
    if (currentMerchantId == null && firstOptionId != null) {
      setCurrentMerchant(firstOptionId, firstOptionSlug);
    }
  }, [currentMerchantId, firstOptionId, firstOptionSlug, setCurrentMerchant]);

  // When opening, seed activeIdx to the currently-selected option (or 0)
  // and move keyboard focus to the listbox container itself. DOM focus
  // stays on the container for the entire open lifetime; the "active"
  // option is communicated via aria-activedescendant.
  useEffect(() => {
    if (!open) return;
    const selIdx = options.findIndex((o) => o.id === currentMerchantId);
    const idx = selIdx >= 0 ? selIdx : 0;
    setActiveIdx(idx);
    // Focus after the next paint so the listbox is mounted.
    const handle = window.setTimeout(() => {
      listboxRef.current?.focus();
    }, 0);
    return () => window.clearTimeout(handle);
    // Intentionally depending only on `open` — re-seeding on every options
    // identity change would yank focus during navigation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Always render and always allow opening the dropdown. When options is
  // empty, the dropdown body shows a clear empty-state instead of leaving
  // the operator staring at a greyed-out button with no explanation.
  const current = options.find((o) => o.id === currentMerchantId);
  const buttonLabel = current
    ? current.label
    : isAdmin
      ? options.length === 0
        ? "No merchants"
        : "Select merchant"
      : "No merchant";

  function commit(idx: number) {
    const opt = options[idx];
    if (!opt) return;
    setCurrentMerchant(opt.id, opt.slug);
    setOpen(false);
    // Return focus to the trigger so the keyboard user keeps continuity.
    triggerRef.current?.focus();
  }

  function closeAndRestoreFocus() {
    setOpen(false);
    triggerRef.current?.focus();
  }

  function onTriggerKeyDown(e: ReactKeyboardEvent<HTMLButtonElement>) {
    // ArrowDown / ArrowUp on the closed trigger opens the listbox and
    // hands focus to it — APG combobox/listbox pattern.
    if (!open && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
      e.preventDefault();
      setOpen(true);
    }
  }

  function onListboxKeyDown(e: ReactKeyboardEvent<HTMLDivElement>) {
    if (options.length === 0) {
      if (e.key === "Escape") {
        e.preventDefault();
        closeAndRestoreFocus();
      }
      return;
    }
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setActiveIdx((i) => (i + 1) % options.length);
        break;
      case "ArrowUp":
        e.preventDefault();
        setActiveIdx((i) => (i - 1 + options.length) % options.length);
        break;
      case "Home":
        e.preventDefault();
        setActiveIdx(0);
        break;
      case "End":
        e.preventDefault();
        setActiveIdx(options.length - 1);
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        commit(activeIdx);
        break;
      case "Escape":
        e.preventDefault();
        closeAndRestoreFocus();
        break;
      case "Tab":
        // Tab leaves the dropdown — close it so focus moves out cleanly.
        closeAndRestoreFocus();
        break;
    }
  }

  // Prevents an option-button mousedown from stealing DOM focus from the
  // listbox container. Under aria-activedescendant the listbox is the sole
  // holder of focus while open — without this preventDefault, mousedown
  // would synchronously transfer focus to the clicked button before the
  // click handler fires, briefly flashing a :focus-visible ring on it and
  // confusing screen readers about what owns focus.
  function onOptionMouseDown(e: ReactMouseEvent<HTMLButtonElement>) {
    e.preventDefault();
  }

  // One-click route to the selected merchant, from any screen. The switcher
  // is the only merchant-context control that's on screen everywhere, so the
  // "go to this merchant" affordance belongs on it — the alternative was
  // Merchants → find the row → open it, or a nav entry that only exists on
  // some screens. `?edit=1` opens the settings modal on arrival.
  //
  // Derived from the STORE, not from `current`. `current` is a lookup into
  // `options`, which for an admin is the GET /v1/merchants response — so
  // keying the shortcut off it meant no shortcut at all whenever that list
  // was still loading, or had failed (an expired session, a 403, a slow
  // network). The selected merchant id+slug are persisted in localStorage
  // and survive all of that: if there's a merchant in context, there's a
  // link to it.
  const linkTarget = currentMerchantSlug || currentMerchantId;
  const currentHref =
    linkTarget != null ? `/merchants/${linkTarget}` : null;
  const currentLabel = current?.label ?? "this merchant";

  return (
    <div
      ref={wrapRef}
      style={{ position: "relative", display: "flex", gap: 4 }}
    >
      <button
        ref={triggerRef}
        type="button"
        className="btn ghost sm"
        onClick={() => setOpen((o) => !o)}
        onKeyDown={onTriggerKeyDown}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        // AUDIT-PASS-6 P-1: the visible <span> below ellipses at 160px, so
        // a long merchant name like "Acme Corporation Private Limited"
        // renders as "Acme Corporation Pri…" and screen readers announce
        // the truncated string. aria-label here carries the full, untrimmed
        // name to AT — sighted users still see the ellipsis, screen-reader
        // users hear the whole thing. Prefixed with "Active merchant: " to
        // clarify what the control does (vs the button just announcing
        // "Acme Corp" with no role context).
        aria-label={
          options.length === 0
            ? "No merchants — open to see why"
            : `Active merchant: ${buttonLabel}. Click to switch.`
        }
        title={
          options.length === 0
            ? "No merchants — open to see why"
            : "Switch active merchant"
        }
      >
        <I.Merchants size={12} />
        <span
          style={{
            maxWidth: 160,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {buttonLabel}
        </span>
        <I.ChevronDown size={11} />
      </button>

      {/* Sits OUTSIDE the trigger — nesting a link inside the combobox
          button would swallow its click and break the listbox pattern. */}
      {currentHref && (
        <Link
          to={`${currentHref}?edit=1`}
          className="btn ghost sm"
          aria-label={`Settings for ${currentLabel}`}
          title={`Settings for ${currentLabel}`}
        >
          <I.Settings size={12} />
        </Link>
      )}

      {open && (
        <div
          ref={listboxRef}
          id={listboxId}
          role="listbox"
          tabIndex={-1}
          aria-activedescendant={
            options[activeIdx]
              ? `${listboxId}-opt-${options[activeIdx].id}`
              : undefined
          }
          onKeyDown={onListboxKeyDown}
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            right: 0,
            minWidth: 220,
            maxHeight: 320,
            overflowY: "auto",
            background: "var(--bg)",
            border: "1px solid var(--hair)",
            borderRadius: 4,
            // UI #20 fix: dark-mode-aware shadow via color-mix (matches the
            // .modal shadow in styles.css). Hardcoded rgba(0,0,0,0.18) here
            // would produce an incorrect heavy shadow in dark mode where
            // --ink is a light colour.
            boxShadow:
              "0 8px 24px color-mix(in oklch, var(--ink) 22%, transparent)",
            zIndex: 50,
            padding: 4,
            // Suppress the default focus ring on the listbox container —
            // the keyboard-focus cue under the activedescendant pattern
            // lives on the active option (via the `focused` style below),
            // not the container. The container owning DOM focus is an
            // implementation detail, not user-visible state.
            outline: "none",
          }}
        >
          {options.length === 0 ? (
            <div className="dim" style={{ padding: "10px 12px", fontSize: 12 }}>
              No merchants available.
            </div>
          ) : (
            options.map((o, idx) => {
              const active = o.id === currentMerchantId;
              const focused = idx === activeIdx;
              return (
                <button
                  key={o.id}
                  id={`${listboxId}-opt-${o.id}`}
                  type="button"
                  role="option"
                  tabIndex={-1}
                  aria-selected={active}
                  onClick={() => commit(idx)}
                  onMouseDown={onOptionMouseDown}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    width: "100%",
                    textAlign: "left",
                    padding: "8px 10px",
                    border: "none",
                    borderRadius: 3,
                    // Visual cue for keyboard-active option (distinct from
                    // the persisted selection). Both can be true at once.
                    background: active
                      ? "var(--tint)"
                      : focused
                        ? "var(--hair)"
                        : "transparent",
                    color: "var(--ink)",
                    fontSize: 12,
                    cursor: "pointer",
                  }}
                >
                  <span
                    style={{
                      flex: 1,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {o.label}
                  </span>
                  {active && <I.Check size={11} stroke={2.5} />}
                </button>
              );
            })
          )}
          {currentHref && (
            <div
              style={{
                borderTop: "1px solid var(--hair)",
                marginTop: 4,
                paddingTop: 4,
              }}
            >
              {/* Not a role="option" — it navigates instead of selecting, so
                  it stays out of the listbox's arrow-key cycle and is reached
                  by Tab (which closes the dropdown first). */}
              <Link
                to={currentHref}
                onClick={() => setOpen(false)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "8px 10px",
                  borderRadius: 3,
                  fontSize: 12,
                  color: "var(--ink-2)",
                }}
              >
                <I.ArrowUpRight size={11} />
                Open {currentLabel}
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
